import { useState, type InputHTMLAttributes } from "react";
import { cn } from "../lib/utils";

export type PasswordStrength = "weak" | "fair" | "strong" | "very-strong";

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  showStrength?: boolean;
  strengthLabel?: boolean;
}

function getStrength(pw: string): { level: PasswordStrength; score: number } {
  if (!pw) return { level: "weak", score: 0 };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const levels: PasswordStrength[] = ["weak", "weak", "fair", "strong", "very-strong", "very-strong"];
  return { level: levels[score] ?? "very-strong", score };
}

const strengthLabels: Record<PasswordStrength, string> = {
  weak: "Weak",
  fair: "Fair",
  strong: "Strong",
  "very-strong": "Very strong",
};

export function PasswordInput({
  className,
  showStrength,
  strengthLabel,
  value,
  defaultValue,
  onChange,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const [internalVal, setInternalVal] = useState((defaultValue as string) ?? "");

  const controlled = value !== undefined;
  const displayVal = controlled ? (value as string) : internalVal;
  const { level, score } = getStrength(displayVal);

  return (
    <div className={cn("pw-wrap", className)}>
      <div className="pw-field">
        <input
          {...props}
          type={visible ? "text" : "password"}
          value={controlled ? value : internalVal}
          onChange={e => {
            if (!controlled) setInternalVal(e.target.value);
            onChange?.(e);
          }}
          className="pw-input"
          autoComplete={props.autoComplete ?? "new-password"}
        />
        <button
          type="button"
          className="pw-toggle"
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible(v => !v)}
          tabIndex={-1}
        >
          {visible ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
      {showStrength && displayVal.length > 0 && (
        <div className="pw-strength">
          <div className="pw-bars">
            {[0, 1, 2, 3].map(i => (
              <span key={i} className={cn("pw-bar", score > i ? `pw-bar-${level}` : "")} />
            ))}
          </div>
          {strengthLabel && (
            <span className={cn("pw-label", `pw-label-${level}`)}>{strengthLabels[level]}</span>
          )}
        </div>
      )}
    </div>
  );
}
